/* eslint-disable import/no-restricted-paths */
import { useApolloClient } from "@apollo/client";
import { useCallback, useState } from "react";

import { uploadPublicImg } from "@/lib/aws-amplify/host-storage";
import { getURL } from "@/lib/aws-amplify/storage";
import type { InputMaybe } from "@/lib/gql/__generated__/graphql";

import type { ForNodeType } from "@/components/features/_model/types";
import { imageMastInteractor } from "@/components/features/snack/_shared/repository/image-mast";

import type { DesignSnackLayoutSettingsFormModelOnSetViewModelType } from "../..";
import type { DesignSnackBlocksFormModel } from "../../type";
import type { S3Object, Scalars } from "@CHILLNN-Inc/chillnn-abr-core";
import type { StudioImageMast } from "@CHILLNN-Inc/chillnn-abr-studio";

type Props = {
	viewModel: DesignSnackBlocksFormModel["ViewModel"];
	onSetViewModel: DesignSnackLayoutSettingsFormModelOnSetViewModelType;
};

export type SnackBlocksFormModelBgImageMethodsType = {
	isShowSelectBgImageModal: boolean;
	activateSelectBgImageModal: () => void;
	deactivateSelectBgImageModal: () => void;
	onOpenChange: (input: boolean) => void;

	bgImage: ForNodeType<InputMaybe<string> | undefined> | undefined;
	onSetBgImage: (input: string) => void;

	imageRepository: () => Promise<StudioImageMast[]>;
	imageUploader: (image: File) => Promise<Scalars["AWSURL"]>;
};

export const useSnackSettingsFormModelBgImageMethods = ({
	viewModel,
	onSetViewModel,
}: Props): SnackBlocksFormModelBgImageMethodsType => {
	const { bgImage, hotelID, companyID } = viewModel;

	const [isShowSelectBgImageModal, setIsShowSelectBgImageModal] = useState<boolean>(false);
	const activateSelectBgImageModal = useCallback(() => {
		setIsShowSelectBgImageModal(true);
	}, [setIsShowSelectBgImageModal]);
	const deactivateSelectBgImageModal = useCallback(() => {
		setIsShowSelectBgImageModal(false);
	}, [setIsShowSelectBgImageModal]);

	const onOpenChange = useCallback(
		(input: boolean) => {
			setIsShowSelectBgImageModal(input);
		},
		[setIsShowSelectBgImageModal],
	);

	const onSetBgImage = useCallback(
		(input: string) => {
			onSetViewModel([
				{
					key: "bgImage",
					value: {
						isEdited: true,
						value: input,
					},
				},
			]);
		},
		[onSetViewModel],
	);

	const apolloClient = useApolloClient();

	const imageRepository = useCallback<SnackBlocksFormModelBgImageMethodsType["imageRepository"]>(async () => {
		const imageMasts = await imageMastInteractor(apolloClient).fetchMast(hotelID);

		return imageMasts.map((imageMast) => {
			return {
				...imageMast,
				src: getURL(imageMast.image),
			} satisfies StudioImageMast;
		});
	}, [apolloClient, hotelID]);

	const imageUploader = useCallback<SnackBlocksFormModelBgImageMethodsType["imageUploader"]>(
		async function (image: File): Promise<Scalars["AWSURL"]> {
			const path = `snack/blockBgImg/${companyID}/${hotelID}`;
			const fileName = `${new Date().getTime()}.blockBgImg`;
			const imageMast = await uploadPublicImg(path, fileName, image);
			await imageMastInteractor(apolloClient).addNewImage(hotelID, imageMast);

			return getURL({
				bucket: imageMast.bucket,
				key: imageMast.key,
				region: imageMast.region,
				mimeType: imageMast.mimeType,
				fileName: imageMast.fileName,
			} satisfies S3Object);
		},
		[apolloClient, companyID, hotelID],
	);

	return {
		isShowSelectBgImageModal,
		activateSelectBgImageModal,
		deactivateSelectBgImageModal,
		onOpenChange,

		bgImage,
		onSetBgImage,

		imageRepository,
		imageUploader,
	};
};
